import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Collapse, Typography } from '@mui/material';
import Undo from '@mui/icons-material/Undo';
import Redo from '@mui/icons-material/Redo';
import difference from '@turf/difference';
import intersects from '@turf/boolean-intersects';
import buffer from '@turf/buffer';
import * as turf from '@turf/helpers';
import { Polygon } from '../../icons/Polygon';
import { SplitGeometry } from '../../icons/SplitGeometry';
import { Explode } from '../../icons/Explode';
import { Cursor } from '../../icons/Cursor';
import {
  actions,
  selectors,
} from '../../store/dux/field-boundary-management-dux';
import { actions as alertActions } from '../../store/dux/alerts-dux';
import {
  useDrawingManager,
  polygonOptions,
} from '../../components/google/maps/use-drawing-manager';
import * as google from '../../components/google/maps/utils';
import { useBoundaryChangeStack } from './use-boundary-change-stack';

export function BoundaryEditControls({ map, field }) {
  const dispatch = useDispatch();
  const isWorking = useSelector(selectors.isWorking);
  const [mode, setMode] = useState(field.shape ? 'edit' : 'polygon');
  const changeStack = useBoundaryChangeStack();
  const drawingManager = useDrawingManager(map);

  const pushChange = useCallback(
    () =>
      drawingManager
        .toGeoJson()
        .then((geoJson) => dispatch(actions.pushBoundaryChange(geoJson))),
    [drawingManager, dispatch],
  );

  const registerPolygon = useCallback(
    (polygon) => {
      polygon.setOptions({ map, ...polygonOptions });

      function onRightClick(e) {
        const { path, vertex = -1 } = e;
        if (vertex < 0) return;

        const paths = polygon.getPaths();
        const targetPath = paths.getAt(path);
        targetPath.removeAt(vertex);
        if (!targetPath.length) paths.removeAt(path);
      }

      const listeners = polygon
        .getPaths()
        .getArray()
        .flatMap((path) => [
          google.addListener(path, 'set_at', pushChange),
          google.addListener(path, 'remove_at', pushChange),
          google.addListener(path, 'insert_at', pushChange),
        ]);

      drawingManager.registerPolygon(
        polygon,
        listeners.concat([
          google.addListener(polygon, 'contextmenu', onRightClick),
        ]),
      );
    },
    [drawingManager, map, pushChange],
  );

  const deserializeGeoJson = useCallback(
    (geometry) => {
      google.geoJsonToPolygons(geometry).forEach((p) => registerPolygon(p));
    },
    [registerPolygon],
  );

  const addDrawnPolygon = useCallback(
    (polygon) => {
      registerPolygon(polygon);
      pushChange();
    },
    [registerPolygon, pushChange],
  );

  const removeDrawnPolygon = useCallback(
    async (polygon) => {
      polygon.setMap(null);
      const mask = await google.polygonToGeoJson(polygon);
      const { features } = await drawingManager.toGeoJson();
      const intersections = features.filter(intersects.bind(null, mask));

      if (intersections.length) {
        intersections.forEach((feature) => {
          drawingManager.deregisterPolygon(feature.properties.id);
          const remains = difference(turf.featureCollection([feature, mask]));
          return remains && deserializeGeoJson(remains);
        });

        pushChange();
      }
    },
    [drawingManager, deserializeGeoJson, pushChange],
  );

  const polygonDrawn = useCallback(
    (polygon) =>
      mode.startsWith('remove')
        ? removeDrawnPolygon(polygon)
        : addDrawnPolygon(polygon),
    [mode, removeDrawnPolygon, addDrawnPolygon],
  );

  const lineDrawn = useCallback(
    async (polyline) => {
      polyline.setMap(null);
      const splits = [[], []];
      const line = buffer(google.polylineToGeoJson(polyline), 0.000001);
      const toPolygon = (coordinates) => ({ type: 'Polygon', coordinates });
      const { features } = await drawingManager.toGeoJson();

      features.forEach((feature) => {
        if (!intersects(feature, line)) return splits[0].push(feature);
        const split = difference(turf.featureCollection([feature, line]));
        if (split.geometry.type === 'Polygon') return splits[0].push(feature);
        const [splitOut, ...rest] = split.geometry.coordinates;
        splits[1].push(toPolygon(splitOut));
        splits[0].push(...rest.map(toPolygon));
      });

      if (splits[1].length) {
        dispatch(actions.splitField(field, ...splits));
      } else {
        dispatch(alertActions.showInfo('Line must fully intersect field'));
      }
    },
    [dispatch, drawingManager, field],
  );

  useEffect(syncMount, [dispatch, map]);
  useEffect(syncDrawingMode, [drawingManager, mode]);
  useEffect(syncPolygonDrawer, [drawingManager, polygonDrawn]);
  useEffect(syncLineDrawer, [drawingManager, lineDrawn]);
  useEffect(syncStackChange, [changeStack, drawingManager, deserializeGeoJson]);
  useEffect(syncField, [drawingManager, field, deserializeGeoJson, pushChange]);

  return (
    <Collapse in>
      <Box
        data-testid="boundary-editor-controls"
        sx={{
          pointerEvents: 'auto',
          display: 'flex',
          justifyContent: 'flex-end',
          bgcolor: 'white',
          color: '#666',
          height: 55,
          px: -0.5,
          '& button:hover': { color: '#333' },
          '& button': { borderRadius: 0, mt: 0, width: 65 },
          '& button.active': {
            color: 'white',
            bgcolor: (theme) => theme.palette.primary.main,
          },
        }}
      >
        {buildButton('Reshape', Cursor, {
          mode: 'edit',
          disabled: !changeStack.current,
        })}
        {buildButton('Add', Polygon, {
          mode: 'polygon',
        })}
        {buildButton('Remove', Polygon, {
          mode: 'remove-polygon',
          disabled: !changeStack.current,
        })}
        {buildButton('Split', SplitGeometry, {
          mode: 'polyline',
          disabled: !field.id,
        })}
        {buildButton('Explode', Explode, {
          disabled:
            !field.id || changeStack.current?.geometry?.type !== 'MultiPolygon',
          onClick: () =>
            dispatch(actions.explodeField(field, changeStack.current)),
        })}
        {buildButton('Undo', Undo, {
          disabled: !changeStack.moveBack,
          onClick: changeStack.moveBack || undefined,
        })}
        {buildButton('Redo', Redo, {
          disabled: !changeStack.moveForward,
          onClick: changeStack.moveForward || undefined,
        })}
      </Box>
    </Collapse>
  );

  function buildButton(text, Icon, props = {}) {
    const { mode: buttonMode, disabled, ...rest } = props;
    const className = buttonMode === mode ? 'active' : '';
    const onClick = buttonMode ? () => setMode(buttonMode) : null;
    const testId = [text.toLowerCase(), buttonMode].filter(Boolean).join('-');

    return (
      <Button
        onClick={onClick}
        color="inherit"
        sx={{ mt: 0.5 }}
        className={className}
        data-testid={testId}
        disabled={disabled || isWorking}
        {...rest}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Icon />
          <Typography variant="caption">{text}</Typography>
        </Box>
      </Button>
    );
  }

  function syncStackChange() {
    if (changeStack.hasMoved) {
      drawingManager.deregisterAllPolygons();
      deserializeGeoJson(changeStack.current);
    }
  }

  function syncMount() {
    map.getDiv()?.classList?.add('editing');
    dispatch(actions.resetBoundaryChanges(map));
  }

  function syncField() {
    if (drawingManager && field.shape) {
      deserializeGeoJson(field.shape);
      pushChange();
    }
  }

  function syncPolygonDrawer() {
    return drawingManager?.listenFor?.('polygoncomplete', polygonDrawn);
  }

  function syncLineDrawer() {
    return drawingManager?.listenFor?.('polylinecomplete', lineDrawn);
  }

  function syncDrawingMode() {
    if (drawingManager) {
      const googleMode = /(polygon|polyline)/.exec(mode);
      drawingManager.setMode(googleMode ? googleMode[1] : null);
    }
  }
}
